import { fm } from '@/appLogic/fileManager/baseFileManager';
import { filterStringNumberUndefinedToString } from '@/coreDomainsModule/extends/string';
import { ExcelRow } from '@/coreDomainsModule/translateExcel';
import { utils, WorkSheet } from 'xlsx';
import XLSX from 'xlsx';
import {
  TranslateExcelOrder,
  TranslateExcelOrderBusiness,
  TranslateExcelOrderBusinessResult,
} from '../coreDomainsModule/translateExcelOrder';
import { TranslateExcelOrderBusinessFactory } from './factories/TranslateExcelOrderBusinessFactory';
import { TranslateExcelOrderIdManager } from './translateOrderIdManager';
import { NJP_IMPORT_SHEET_NAME } from './constants';

/**
 * Excelファイルを解析してExcelRow型の配列に変換する関数
 * @param fileName - ファイル名
 * @param sheetName - シート名
 * @param worksheet - Excelシートデータ
 * @param headerRowIndex - 列名として使う行（0が最初の行）
 * @returns ExcelRowの配列
 */
const parseExcelSheet = (fileName: string, sheetName: string, worksheet: WorkSheet, headerRowIndex = 0): ExcelRow[] => {
  const rows: ExcelRow[] = [];
  const sheetData = utils.sheet_to_json(worksheet, { header: 1 }) as (string | number)[][];

  // 1行目を列名として取得
  const columnHeaders = sheetData[headerRowIndex];

  // 1行目以降をデータ行として処理
  for (let rowIndex = headerRowIndex + 1; rowIndex < sheetData.length; rowIndex++) {
    const row = sheetData[rowIndex];
    const rowData: ExcelRow = {};

    row.forEach((cell, colIndex) => {
      const cellAddress = utils.encode_cell({ c: colIndex, r: rowIndex }); // セルのアドレス (例: "A2", "B2"など)
      const columnName = (columnHeaders[colIndex] as string) || `Column${colIndex + 1}`; // 列名がない場合に備えてfallback

      // セル情報を取得（仮に `worksheet[cellAddress]` でセルデータを取得するとして）
      const cellInfo = worksheet[cellAddress];
      const cellValue = cellInfo?.w || cellInfo?.v; // `w` があればそれを使い、なければ `v` を使用

      rowData[columnName] = {
        value: cellValue,
        address: cellAddress,
        fileName: fileName,
        sheetName: sheetName,
      };
    });

    rows.push(rowData);
  }

  return rows;
};

/**
/**
 * ファイル情報を読み込み、Excelデータを解析する関数
 * バリデーションはBEに委譲
 * @param file - ファイル情報
 */
export const handleFileInput = async (file: File): Promise<TranslateExcelOrderBusinessResult> => {
  const ERROR_RESULT = { splitOrderExcelData: {}, isSuccess: false };
  if (!file) {
    console.error('No file selected');
    return ERROR_RESULT;
  }

  let splitResult: Record<string, TranslateExcelOrderBusiness>;

  try {
    // ファイルをArrayBufferに変換する
    const data = await fm.convertFileToBuffer(file);
    if (data) {
      const workbook = XLSX.read(data, { type: 'array' });
      const excelData: ExcelRow[] = [];

      // 各シートを解析してExcelRow型に変換
      workbook.SheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];
        // 基本ヘッダーは1行目、NJP輸入書式の場合ヘッダーは2行目になる
        const isNJPImportFormatHeaderRow = sheetName === NJP_IMPORT_SHEET_NAME ? 1 : 0;
        const parsedRows = parseExcelSheet(file.name, sheetName, worksheet, isNJPImportFormatHeaderRow);
        excelData.push(...parsedRows);
      });

      // ここでexcelDataを使って必要な処理を行う
      splitResult = splitOrderExcelData(excelData);
      return { splitOrderExcelData: splitResult, isSuccess: true };
    } else {
      console.error('Failed to read file as buffer');
      splitResult = {};
      return ERROR_RESULT;
    }
  } catch (error) {
    console.error('Error reading file:', error);
    splitResult = {};
    return ERROR_RESULT;
  }
};

/**
 * 発注エクセルから取り込んだ情報を整形する
 * @param excelData - エクセルから取り込んだ情報
 */
const splitOrderExcelData = (excelData: ExcelRow[]): Record<string, TranslateExcelOrderBusiness> => {
  const inputFormArray: Record<string, TranslateExcelOrderBusiness> = {};

  // 初期化
  const initialExcelOrderData = TranslateExcelOrderBusinessFactory.create();
  excelData.forEach((data, idx) => {
    // 基本的にfileNameは同じはずなので最初の1つ目を取得
    const fileName = (data?.[Object.keys(data)?.[0]]?.fileName ?? '').toString();
    // 基本的にsheetNameは同じはずなので最初の1つ目を取得
    const sheetName = (data?.[Object.keys(data)?.[0]]?.sheetName ?? '').toString();

    if (fileName === undefined || sheetName === undefined) {
      throw Error();
    }

    // 発注情報に紐づく集計情報を区別するために「置場ｺｰﾄﾞ、容量、商品ｺｰﾄﾞ、仕入先ｺｰﾄﾞ」を使用してkeyの作成を行う
    const excelOrderBusiness: TranslateExcelOrderBusiness = structuredClone(initialExcelOrderData);
    excelOrderBusiness.warehouseNumber = filterStringNumberUndefinedToString(data['納入先CD2']?.value ?? data['置場ｺｰﾄﾞ']?.value);
    excelOrderBusiness.warehouse = filterStringNumberUndefinedToString(data['納入先名2_1']?.value ?? data['置場名']?.value);
    excelOrderBusiness.package = filterStringNumberUndefinedToString(data['入目']?.value ?? data['容量']?.value);
    excelOrderBusiness.businessNumber = filterStringNumberUndefinedToString(data['商品CD']?.value ?? data['商品ｺｰﾄﾞ']?.value);
    excelOrderBusiness.businessName = filterStringNumberUndefinedToString(data['商品名']?.value);
    excelOrderBusiness.supplierNumber = filterStringNumberUndefinedToString(
      data['仕入先CD']?.value ?? data['入荷先ｺｰﾄﾞ']?.value ?? data['仕入先ｺｰﾄﾞ']?.value
    );
    excelOrderBusiness.supplierName = filterStringNumberUndefinedToString(data['入荷先名']?.value ?? data['仕入先名']?.value);

    // IDは上記の複合キーとする
    excelOrderBusiness.translateOrderBusinessId = TranslateExcelOrderIdManager.generateOrderIdFormExcelInput(excelOrderBusiness);

    // 集計が存在する場合のみ次の処理に移行する
    if (excelOrderBusiness.translateOrderBusinessId) {
      // 読み取った発注情報を入れるための変数を初期化する
      const excelOrder: TranslateExcelOrder = {
        crossDate: '',
        amount: 0,
        fixOrderDate: '',
        orderIssuedNumber: '',
        orderNumber: '',
        status: 'ApprovedAwaitingDeliveryDate',
        excelInfo: {
          cellNumber: '',
          sheetName: '',
          fileName: '',
        },
      };

      // keyが存在しない時は新規登録
      if (!inputFormArray[excelOrderBusiness.translateOrderBusinessId]) {
        // 置場CD、容量、商品CD、仕入先CDをkeyとして、発注取引情報を格納する
        inputFormArray[excelOrderBusiness.translateOrderBusinessId] = excelOrderBusiness;
      }

      // 以下で発注情報を入れていく
      const step = data['ステップ']?.value;
      if (step) {
        // ステップがある場合NJP輸入フォーマット
        if (step === '1') {
          const orderNumber =
            filterStringNumberUndefinedToString(data['ORDER NO.']?.value) +
            '-' +
            filterStringNumberUndefinedToString(data['明細番号']?.value);

          if (
            Object.values(inputFormArray[excelOrderBusiness.translateOrderBusinessId].excelOrders).some(
              (o) => o.orderNumber === orderNumber && o.status === 'Confirmed'
            )
          ) {
            // STEP2がある場合STEP1は消す
            return;
          }

          excelOrder.orderNumber = orderNumber;
          excelOrder.amount = Number(data['発注数量']?.value);
          excelOrder.crossDate = filterStringNumberUndefinedToString(data['REQUEST ARRIVAL DATE']?.value);
          excelOrder.status = 'ApprovedAwaitingDeliveryDate';
        } else if (step === '2') {
          const orderNumber =
            filterStringNumberUndefinedToString(data['PO NO.']?.value) +
            '-' +
            filterStringNumberUndefinedToString(data['PO明細番号']?.value);

          // STEP2がある場合STEP1は消す
          const excelOrders = inputFormArray[excelOrderBusiness.translateOrderBusinessId].excelOrders;
          inputFormArray[excelOrderBusiness.translateOrderBusinessId].excelOrders = Object.fromEntries(
            Object.entries(excelOrders).filter(([_, o]) => !(o.orderNumber === orderNumber && o.status === 'ApprovedAwaitingDeliveryDate'))
          );

          excelOrder.orderNumber = orderNumber;
          excelOrder.amount = Number(data['INV数量']?.value);
          excelOrder.crossDate = filterStringNumberUndefinedToString(data['納入希望日']?.value);
          excelOrder.status = 'Confirmed';
        }
      } else {
        // ない場合デフォルトフォーマット
        excelOrder.amount = Number(data['数量']?.value);
        excelOrder.orderNumber = filterStringNumberUndefinedToString(data['伝票NO']?.value);
        excelOrder.status = isRecordedOrder(filterStringNumberUndefinedToString(data['計上区分']?.value ?? data['計上FLG']?.value))
          ? 'ApprovedAwaitingDeliveryDate'
          : 'Delivered';
        excelOrder.crossDate = filterStringNumberUndefinedToString(data['納期/納入日']?.value);
        excelOrder.fixOrderDate = filterStringNumberUndefinedToString(data['受注日']?.value);
        excelOrder.orderIssuedNumber = filterStringNumberUndefinedToString(data['買注文NO']?.value);
      }

      // 発注情報が何もない行は無視する
      if (!excelOrder.amount && !excelOrder.crossDate && !excelOrder.fixOrderDate && !excelOrder.orderNumber) {
        return;
      }

      // 以下でエクセルの情報を発注情報に含める
      excelOrder.excelInfo = getOrderBusinessExcelInfo(data, fileName, sheetName);

      // Indexをkeyとして、発注情報を登録する
      // 重複等のバリデーションはBEで行う
      inputFormArray[excelOrderBusiness.translateOrderBusinessId].excelOrders[idx] = excelOrder;
    }
  });
  return inputFormArray;
};

/**
 * 発注エクセルの情報を取得する
 * @param data - エクセルデータ
 * @param fileName - ファイル名
 * @param sheetName - シート名
 */
const getOrderBusinessExcelInfo = (data: ExcelRow, fileName: string, sheetName: string) => {
  return { cellNumber: String(data['納期/納入日']?.address), sheetName: sheetName, fileName: fileName };
};

/**
 * 計上区分の情報を基に、対象の発注情報が計上済みかどうかを確認する
 * @param classification - 計上区分
 */
const isRecordedOrder = (classification: string): boolean => {
  return classification == null || classification === '';
};
